import React, { useState, useEffect } from "react";

import InfoTooltip from "components/InfoTooltip";
import WarningTooltip from "components/WarningTooltip";
import Button from "components/Button";

import classes from "./style.module.scss";
import sharedClasses from "../style.module.scss";

const getColorClass = (numberOfPeriods) => {
    switch (numberOfPeriods.toString()) {
        case '9':
            return ["#001344", "#0049C6", "#0B91EA", "#0EB0A9", "#18C1AD", "#AC0D34", "#C61717", "#F58020", "#FFB22D"];
        case '8':
            return ["#001344", "#0049C6", "#0B91EA", "#0EB0A9", "#AC0D34", "#C61717", "#F58020", "#FFB22D"];
        case '7':
            return ["#001344", "#0049C6", "#0B91EA", "#AC0D34", "#C61717", "#F58020", "#FFB22D"];
        case '6':
            return ["#001344", "#0049C6", "#0B91EA", "#C61717", "#F58020", "#FFB22D"];
        case '5':
            return ["#001344", "#0049C6", "#C61717", "#F58020", "#FFB22D"];
        case '4':
            return ["#0049C6", "#C61717", "#F58020", "#FFB22D"];
        case '3':
            return ["#0049C6", "#C61717", "#FFB22D"];
        case '2':
            return ["#0049C6", "#FFB22D"];
        case '1':
            return ["#0049C6"];
        default:
            return ["#FFF0F0"]; // default color
    }
};

const getColor = (value, numberOfPeriods) => {
    const colors = getColorClass(numberOfPeriods);
    const index = Math.min(value - 1, colors.length - 1);
    return colors[index];
};

export default ({
    name,
    label,
    input = {},
    meta = {},
    hideLabel,
    large,
    className,
    info = "",
    warning = "",
    quantity = false,
    setValue,
    setTouched = false,
    columnTitles = [],
    rowTitles = [],
    disabled = false,
    disabledButton = false,
    parentError = false,
    style = {},
    numberOfPeriods = 1,
    inputArray = [], // This will now be used for preset values
}) => {
    const [touched, toggleTouched] = useState(false);
    const { invalid = false, error = "" } = meta;
    const { value, onChange } = input;
    const isTouched = touched || setValue || setTouched;

    const classNames = [
        classes.inputField,
        className && className,
        large && classes.large,
        quantity && classes.hasQuantity,
        (parentError || (invalid && isTouched)) && classes.invalid,
    ].filter((c) => c);

    const numCols = columnTitles.length || 1;
    const numRows = rowTitles.length || 1;

    // Initialize selection and previousState as objects
    const initializeSelection = (preset = []) => {
        const selectionObj = {};
        for (let rowIndex = 0; rowIndex < numRows; rowIndex++) {
            selectionObj[rowIndex] = {};
            for (let colIndex = 0; colIndex < numCols; colIndex++) {
                selectionObj[rowIndex][colIndex] = preset[rowIndex]?.[colIndex] || 0; // default value or preset value
            }
        }
        return selectionObj;
    };

    const [previousState, setPreviousState] = useState(initializeSelection());
    const [selection, setSelection] = useState(initializeSelection(inputArray)); // Initialize with inputArray

    useEffect(() => {
        if (inputArray.length > 0) {
            const newSelection = initializeSelection(inputArray);
            setSelection(newSelection);
        }
    }, [inputArray]);
    
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState(null);

    const handleMouseDown = (rowIndex, colIndex) => {
        if (disabled) return; 
    
        setPreviousState(JSON.parse(JSON.stringify(selection))); 
    
        setIsDragging(true);
        setDragStart({ row: rowIndex, col: colIndex });
        highlightSelection(rowIndex, colIndex); 
    };

    const handleMouseOver = (rowIndex, colIndex) => {
        if (disabled || !isDragging) return; 
        highlightSelection(rowIndex, colIndex);
    };

    const handleMouseUp = () => {
        if (disabled) return; 
        setIsDragging(false);
        setDragStart(null);
    };

    // const highlightSelection = (endRow, endCol) => {
    //     if (!dragStart) {
    //         return;
    //     }
    
    //     const startRow = dragStart.row;
    //     const startCol = dragStart.col;
    
    //     const newSelection = { ...selection }; 

    //     for (let rowIndex = Math.min(startRow, endRow); rowIndex <= Math.max(startRow, endRow); rowIndex++) {
    //         for (let colIndex = Math.min(startCol, endCol); colIndex <= Math.max(startCol, endCol); colIndex++) {
    //             newSelection[rowIndex][colIndex] = -1; // selected
    //         }
    //     }
    
    //     setSelection(newSelection);
    // };
    const highlightSelection = (endRow, endCol) => {
        if (!dragStart) return;
    
        const startRow = dragStart.row;
        const startCol = dragStart.col;
    
        const newSelection = Object.fromEntries(
            Object.entries(selection).map(([rowKey, rowValue]) => [
                rowKey,
                { ...rowValue }
            ])
        );
    
        for (let rowIndex = Math.min(startRow, endRow); rowIndex <= Math.max(startRow, endRow); rowIndex++) {
            for (let colIndex = Math.min(startCol, endCol); colIndex <= Math.max(startCol, endCol); colIndex++) {
                newSelection[rowIndex][colIndex] = -1;
            }
        }
    
        setSelection(newSelection);
    };
    
    const applyValueToSelection = (newValue) => {
        if (disabled || newValue < 1 || newValue > numberOfPeriods) {
            return; 
        }

        setPreviousState(JSON.parse(JSON.stringify(selection))); 
    
        const updatedSelection = { ...selection };
        Object.keys(updatedSelection).forEach(row => {
            Object.keys(updatedSelection[row]).forEach(col => {
                if (updatedSelection[row][col] === -1) {
                    updatedSelection[row][col] = newValue;
                }
            });
        });
    
        setSelection(updatedSelection);
        input.onChange(updatedSelection);
    };

    const handleReset = () => {
        if (disabled) return;
        const resetSelection = initializeSelection();
        setSelection(resetSelection);
        input.onChange(resetSelection);
    };

    const handleCancel = () => {
        if (disabled) return; 

        setSelection(JSON.parse(JSON.stringify(previousState)));
    };

    const handleKeyDown = (e) => {
        if (disabled) return; 
        if (e.key === 'Escape') {
            handleCancel();
        } else if (e.key >= '1' && e.key <= numberOfPeriods.toString()) {
            const newValue = parseInt(e.key, 10);
            applyValueToSelection(newValue);
        }
    };

    const handleInputChange = (e, rowIndex, colIndex) => {
        if (disabled) return;
    
        let targetValue = e.target.value;
    
        if (targetValue === "") {
            setSelection((prevSelection) => {
                const updatedSelection = { ...prevSelection };
                updatedSelection[rowIndex][colIndex] = 0; // Or whatever default you want
                return updatedSelection;
            });
            input.onChange(selection); // Update input value
            return;
        }
    
        targetValue = targetValue.replace(/^0+/, ''); // Remove leading zeros
    
        const newValue = parseInt(targetValue, 10);
    
        if (!isNaN(newValue) && newValue >= 1 && newValue <= numberOfPeriods) {
            const updatedSelection = { ...selection };
            updatedSelection[rowIndex][colIndex] = newValue;
    
            setSelection(updatedSelection);
            input.onChange(updatedSelection);
        } else {
            e.target.value = selection[rowIndex][colIndex] !== undefined ? selection[rowIndex][colIndex] : "";
        }
    };
      
    
    useEffect(() => {
        if (setValue || setValue === 0) {
            onChange(setValue);
        }
    }, [setValue]);

    return (
        <div className={classNames.join(" ")} style={style} onMouseUp={handleMouseUp} onMouseLeave={handleMouseUp}>
            <label className={hideLabel && "invisible"} htmlFor={name}>
                {label}
            </label>
            {info && <InfoTooltip className={classes.infoTooltip} info={info} />}
            {warning && !disabled && (
                <WarningTooltip className={`${classes.warningTooltip} ${info && classes.pushLeft}`} warning={warning} />
            )}
            <div className={`${classes.gridContainer} ${sharedClasses.containerClass} ${warning && !disabled ? classes.warningContainer : ''}`}>
                <div className={classes.gridHeader}>
                    <div className={classes.rowHeader}></div>
                    {columnTitles.map((header, index) => (
                        <div key={index} className={classes.gridHeaderCell}>
                            {header}
                        </div>
                    ))}
                </div>

                {Object.keys(selection).map((rowIndex) => (
                    <div key={rowIndex} className={classes.gridRow}>
                        <div className={classes.rowHeader}>
                            {rowTitles[rowIndex] || ""}
                        </div>
                        {Object.keys(selection[rowIndex]).map((colIndex) => {
                            const cell = selection[rowIndex][colIndex];
                            const color = (disabled)
                                ? getColor(0, numberOfPeriods) 
                                : (cell === -1)
                                    ? "#646F81" 
                                    : getColor(cell, numberOfPeriods);  

                            const cellClassNames = [
                                classes.gridCell,
                                disabled && classes.disabled 
                            ].filter(Boolean).join(' ');

                            return (
                                <div
                                    key={colIndex}
                                    className={cellClassNames}
                                    style={{ backgroundColor: color }}
                                    onMouseDown={() => handleMouseDown(parseInt(rowIndex), parseInt(colIndex))}
                                    onMouseOver={() => handleMouseOver(parseInt(rowIndex), parseInt(colIndex))}
                                >
                                    <input
                                        type="text" // Change to text to manage input better
                                        value={cell === -1 ? "" : cell}
                                        disabled={disabled}
                                        onChange={(e) => handleInputChange(e, parseInt(rowIndex), parseInt(colIndex))}
                                        onKeyDown={handleKeyDown}
                                        // You can also add 'onFocus' here if you want additional handling
                                    />
                                </div>
                            );
                        })}
                    </div>
                ))}
            </div>
            <Button
                type="button"
                className={`${classes.resetButton} ${sharedClasses.buttonClass}`}
                onClick={handleReset}
                disabled={disabled || disabledButton}
            >
                Reset
            </Button>
        </div>
    );
};
